import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Moon, Sparkles, Heart, Calendar, Star, Flower2 } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/App";

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const features = [
    {
      icon: <Calendar className="w-8 h-8" />,
      title: "Cycle Tracking",
      description: "Track your menstrual cycle with intuitive calendar logging"
    },
    {
      icon: <Moon className="w-8 h-8" />,
      title: "Moon Integration",
      description: "Discover how lunar phases influence your cycle"
    },
    {
      icon: <Flower2 className="w-8 h-8" />,
      title: "Herbal Remedies",
      description: "Natural remedies tailored to each phase"
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: "Astrology",
      description: "Personal birth chart and cosmic insights"
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Sacred Rituals",
      description: "Herbs, oils, and crystals for each phase"
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "AI Guidance",
      description: "Personalized wellness tips powered by AI"
    }
  ];

  return (
    <div className="min-h-screen relative z-10">
      {/* Hero Section */}
      <section className="container mx-auto px-4 pt-20 pb-16 md:pt-32 md:pb-24">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto"
        >
          <div className="flex justify-center mb-6">
            <div className="relative">
              <img src="/logo.png" alt="Sacred Celestial Flow" className="w-32 h-32 object-contain animate-float" />
              <div className="absolute inset-0 w-32 h-32 bg-moon-gold/20 rounded-full blur-xl animate-pulse-glow"></div>
            </div>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            <span className="gradient-text">Sacred Celestial Flow</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-mystic-100 mb-4 font-light">
            Align with Your Natural Rhythms
          </p>
          
          <p className="text-base md:text-lg text-mystic-200/80 mb-10 max-w-2xl mx-auto">
            Track your menstrual cycle in harmony with the moon. Discover herbal remedies, 
            sacred rituals, and personalized guidance for every phase of your journey.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {user ? (
              <Button 
                data-testid="go-to-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                className="bg-mystic-300 hover:bg-mystic-200 text-white font-medium py-6 px-10 rounded-full shadow-[0_0_20px_rgba(157,78,221,0.4)] transition-all duration-300 hover:scale-105 text-lg"
              >
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button 
                  data-testid="get-started-btn"
                  onClick={() => navigate('/register')}
                  className="bg-mystic-300 hover:bg-mystic-200 text-white font-medium py-6 px-10 rounded-full shadow-[0_0_20px_rgba(157,78,221,0.4)] transition-all duration-300 hover:scale-105 text-lg"
                >
                  Begin Your Journey
                </Button>
                <Button 
                  data-testid="login-btn"
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="border-mystic-300 text-mystic-100 hover:bg-mystic-300/10 py-6 px-10 rounded-full transition-all duration-300 text-lg"
                >
                  Sign In
                </Button>
              </>
            )}
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
            Embrace Your Cycle
          </h2>
          <p className="text-mystic-200/80 max-w-xl mx-auto">
            Everything you need to understand and honor your body's natural rhythms
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={`k-${index}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="glass rounded-2xl p-6 hover:border-mystic-300/30 transition-all duration-300 group"
            >
              <div className="text-mystic-300 mb-4 group-hover:text-moon-gold transition-colors">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-mystic-200/70">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Moon Connection Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="glass-heavy rounded-3xl p-8 md:p-12 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex justify-center gap-4 mb-6 text-4xl">
              <span className="animate-twinkle" style={{ animationDelay: '0s' }}>🌑</span>
              <span className="animate-twinkle" style={{ animationDelay: '0.3s' }}>🌒</span>
              <span className="animate-twinkle" style={{ animationDelay: '0.6s' }}>🌓</span>
              <span className="animate-twinkle" style={{ animationDelay: '0.9s' }}>🌔</span>
              <span className="animate-twinkle" style={{ animationDelay: '1.2s' }}>🌕</span>
              <span className="animate-twinkle" style={{ animationDelay: '1.5s' }}>🌖</span>
              <span className="animate-twinkle" style={{ animationDelay: '1.8s' }}>🌗</span>
              <span className="animate-twinkle" style={{ animationDelay: '2.1s' }}>🌘</span>
            </div>
            
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">
              The Moon & Your Cycle
            </h2>
            <p className="text-mystic-200/80 max-w-2xl mx-auto mb-8">
              For millennia, women have recognized the connection between lunar cycles and menstrual cycles. 
              Both follow a similar rhythm of approximately 28 days. LunaFlow helps you explore and 
              honor this ancient wisdom.
            </p>
            
            {!user && (
              <Button 
                data-testid="discover-connection-btn"
                onClick={() => navigate('/register')}
                className="bg-moon-gold hover:bg-moon-gold/90 text-mystic-900 font-medium py-3 px-8 rounded-full transition-all duration-300 hover:scale-105"
              >
                Discover the Connection
              </Button>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-8 text-center">
        <p className="text-mystic-200/50 text-sm">
          © 2025 Sacred Celestial Flow. Honor your rhythm.
        </p>
      </footer>
    </div>
  );
};

export default Landing;
